import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';

/**
 * Hook to get unread ticket message count for the current user.
 * Counts admin replies that haven't been read yet.
 * 
 * Optimized: Uses Realtime subscription instead of polling to reduce DB load.
 */
export function useUnreadTicketMessages() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Step 1: Load user's ticket IDs first (dependency for unread count)
  const { data: userTicketIds = [] } = useQuery({
    queryKey: ['user-ticket-ids', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data: tickets, error } = await supabase
        .from('tickets')
        .select('id')
        .eq('metadata->>reporter_user_id', user.id);

      if (error) {
        console.error('[useUnreadTicketMessages] Error fetching ticket IDs:', error);
        return [];
      }

      return tickets?.map(t => t.id) || [];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  // Step 2: Count unread messages AND get first unread ticket ID for direct navigation
  const { data: unreadData = { count: 0, firstTicketId: null } } = useQuery({
    queryKey: ['unread-ticket-messages', user?.id, userTicketIds],
    queryFn: async () => {
      if (!user?.id || userTicketIds.length === 0) return { count: 0, firstTicketId: null };

      // Get unread messages with ticket_id for direct navigation
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('ticket_id')
        .in('ticket_id', userTicketIds)
        .eq('sender_type', 'admin')
        .eq('is_read', false)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[useUnreadTicketMessages] Error fetching unread messages:', error);
        return { count: 0, firstTicketId: null };
      }

      const count = data?.length || 0;
      const firstTicketId = data?.[0]?.ticket_id || null;
      
      return { count, firstTicketId };
    },
    enabled: !!user?.id && userTicketIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 min cache
  });

  // Step 3: Realtime subscription for ticket message updates (replaces polling)
  useEffect(() => {
    if (!user?.id || userTicketIds.length === 0) return;

    const channel = supabase
      .channel('user-ticket-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ticket_messages',
        },
        (payload) => {
          // Only invalidate if the message is for one of user's tickets
          if (userTicketIds.includes(payload.new?.ticket_id)) {
            queryClient.invalidateQueries({ queryKey: ['unread-ticket-messages'] });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ticket_messages',
        },
        (payload) => {
          if (userTicketIds.includes(payload.new?.ticket_id)) {
            queryClient.invalidateQueries({ queryKey: ['unread-ticket-messages'] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, userTicketIds.join(','), queryClient]);

  return { 
    unreadCount: unreadData.count, 
    firstUnreadTicketId: unreadData.firstTicketId 
  };
}
